/* src/pages/TeamSettings.jsx
 * Team management and member settings page
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsMobile } from '../hooks/useIsMobile';
import InlineConfirm from '../components/InlineConfirm';
import { Skeleton, TeamMembersSkeleton } from '../components/Skeleton';
import { apiGet, googleAdsApi, functionsUrl, getAuthHeaders, smsApi, usageApi } from '../utils/api';
import { useWorkspaceSmsLive } from '../hooks/useWorkspaceSmsLive';
import LeadRoutingSettings from '../components/LeadRoutingSettings';

const dangerConfirmBtnStyle = {
  padding: '7px 14px',
  borderRadius: '999px',
  border: '1px solid var(--hairline-5)',
  background: 'var(--surface-3)',
  color: 'var(--danger)',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export default function TeamSettings() {
  const { currentUser } = useAuth();
  const { selectedWorkspaceId, isOwner, workspaceReady } = useWorkspace();
  const { live: workspaceSmsLive } = useWorkspaceSmsLive(selectedWorkspaceId);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Google Ads state
  const [adsStatus, setAdsStatus] = useState(null);
  const [adsLoading, setAdsLoading] = useState(true);
  const [adsError, setAdsError] = useState(null);
  const [adsAccounts, setAdsAccounts] = useState([]);
  const [adsSelectedId, setAdsSelectedId] = useState('');
  const [adsConnecting, setAdsConnecting] = useState(false);
  const [hideAnsweredBy, setHideAnsweredBy] = useState(false);
  const [ownerTier, setOwnerTier] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [adsFinishing, setAdsFinishing] = useState(false);
  const [adsTesting, setAdsTesting] = useState(false);
  const [adsTestResult, setAdsTestResult] = useState(null);
  
  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLeadAccess, setInviteLeadAccess] = useState(false);
  const [inviteAiReplyAccess, setInviteAiReplyAccess] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [pendingResendInvite, setPendingResendInvite] = useState(null);
  const [voiceWaitlist, setVoiceWaitlist] = useState(false);
  const [voiceWaitlistSaving, setVoiceWaitlistSaving] = useState(false);

  /** OAuth return landed on select_account — skip reset/status until finish or cancel. */
  const adsPickerFlowActive = useRef(false);

  function normalizeAdsAccounts(raw) {
    return (raw || []).map((item) =>
      typeof item === 'string'
        ? { customerId: item, descriptiveName: item, isManager: false }
        : item
    );
  }

  function clearAdsPickerFlow() {
    adsPickerFlowActive.current = false;
    setAdsAccounts([]);
    setAdsSelectedId('');
  }

  useEffect(() => {
    if (currentUser && selectedWorkspaceId && workspaceReady) {
      checkAccess();
    }
  }, [currentUser, selectedWorkspaceId, isOwner, workspaceReady]);

  useEffect(() => {
    adsPickerFlowActive.current = false;
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (!selectedWorkspaceId || !isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const [wsSnap, usage] = await Promise.all([
          getDoc(doc(db, 'workspaces', selectedWorkspaceId)),
          usageApi.getUsage(selectedWorkspaceId).catch(() => null),
        ]);
        if (cancelled) return;
        if (wsSnap.exists()) setHideAnsweredBy(!!wsSnap.data()?.hideAnsweredBy);
        if (usage?.tier) setOwnerTier(usage.tier);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [selectedWorkspaceId, isOwner]);

  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId || !isOwner) return;

    // Re-run after navigate() clears ?google_ads=select_account — keep picker state.
    if (adsPickerFlowActive.current) {
      setAdsLoading(false);
      return;
    }

    setAdsLoading(true);
    setAdsError(null);
    setAdsStatus(null);
    setAdsAccounts([]);
    setAdsSelectedId('');
    setAdsTestResult(null);

    async function initGoogleAds() {
      try {
        const params = new URLSearchParams(location.search);
        const googleAdsParam = params.get('google_ads');
        const ws = selectedWorkspaceId;

        if (googleAdsParam === 'select_account') {
          adsPickerFlowActive.current = true;
          const res = await googleAdsApi.listAccounts(ws);
          if (res.error) {
            adsPickerFlowActive.current = false;
            setAdsError(res.error);
          } else {
            setAdsAccounts(normalizeAdsAccounts(res.accounts || res.customers));
          }
          navigate(location.pathname, { replace: true });
        } else if (googleAdsParam === 'error') {
          const reason = params.get('reason') || 'Failed to connect Google Ads';
          navigate(location.pathname, { replace: true });
          setAdsError(reason);
        } else {
          const res = await googleAdsApi.status(ws);
          if (res.error) {
            setAdsError(res.error);
          } else {
            setAdsStatus(res);
          }
        }
      } catch (err) {
        setAdsError('Failed to load Google Ads status');
      } finally {
        setAdsLoading(false);
      }
    }
    initGoogleAds();
  }, [currentUser, selectedWorkspaceId, isOwner, location.search, navigate, location.pathname]);

  async function checkAccess() {
    if (!currentUser || !selectedWorkspaceId) return;

    try {
      if (!isOwner) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      setAccessDenied(false);
      const workspaceDoc = await getDoc(doc(db, 'workspaces', selectedWorkspaceId));
      if (workspaceDoc.exists()) {
        setVoiceWaitlist(workspaceDoc.data()?.voice_waitlist === true);
      }

      loadMembers();
    } catch (error) {
      console.error('Error checking access:', error);
      setAccessDenied(true);
      setLoading(false);
    }
  }

  async function loadMembers() {
    if (!currentUser || !selectedWorkspaceId) return;

    try {
      setLoading(true);
      setError(null);

      const data = await apiGet('/getWorkspaceMembers', { workspaceId: selectedWorkspaceId });

      if (!data.error) {
        setMembers(data.members || []);
      } else {
        setError(data.error || 'Failed to load team members');
      }
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite() {
    if (!currentUser || !inviteEmail) {
      setError('Please enter an email address');
      return;
    }

    // Check if there's already a pending invitation for this email
    const existingPending = members.find(
      m => m.email === inviteEmail && m.status === 'pending'
    );

    if (existingPending) {
      setPendingResendInvite({ id: existingPending.id, email: inviteEmail });
      return;
    }

    try {
      setInviting(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(functionsUrl('/inviteTeamMember'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          workspaceId: selectedWorkspaceId,
          email: inviteEmail,
          name: inviteName.trim() || undefined, // Optional: Only send if provided
          leadAccess: inviteLeadAccess,
          aiReplyAccess: inviteAiReplyAccess,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.emailSent) {
          setSuccess(`Invitation sent to ${inviteEmail}`);
        } else {
          setSuccess(`Invitation created for ${inviteEmail}, but email failed: ${data.emailError || 'Unknown error'}. The invitee can still accept using the invitation link.`);
        }
        setInviteEmail('');
        setInviteName('');
        setInviteLeadAccess(false);
        setInviteAiReplyAccess(false);
        setShowInviteForm(false);
        loadMembers(); // Refresh list
      } else {
        setError(data.error || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  }

  async function handleResendInvite(inviteId) {
    if (!currentUser) return;

    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(functionsUrl('/resendInvite'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          inviteId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.emailSent) {
          setSuccess('Invitation email resent successfully');
        } else {
          setSuccess(`Invitation email resend failed: ${data.emailError || 'Unknown error'}. The invitee can still use the original invitation link.`);
        }
        loadMembers();
      } else {
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (err) {
      console.error('Error resending invitation:', err);
      setError('Failed to resend invitation');
    }
  }

  async function handleRemoveMember(memberId) {
    if (!currentUser || !selectedWorkspaceId) return;
    
    // Validate memberId
    if (!memberId) {
      setError('Invalid member ID');
      return;
    }
    
    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(functionsUrl('/removeTeamMember'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          workspaceId: selectedWorkspaceId,
          memberId, // Backend handles both userId and document id
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Team member removed successfully');
        loadMembers();
      } else {
        console.error('[TEAM SETTINGS] Remove member error:', data);
        setError(data.error || data.message || 'Failed to remove team member');
      }
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove team member: ' + (err.message || 'Unknown error'));
    }
  }

  async function handleUpdatePermissions(memberId, leadAccess, aiReplyAccess) {
    if (!currentUser || !selectedWorkspaceId) return;

    // Validate memberId
    if (!memberId) {
      setError('Invalid member ID');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(functionsUrl('/updateMemberPermissions'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          workspaceId: selectedWorkspaceId,
          memberId, // Backend handles both userId and document id
          leadAccess,
          aiReplyAccess,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Member permissions updated successfully');
        loadMembers();
      } else {
        console.error('[TEAM SETTINGS] Update permissions error:', data);
        setError(data.error || data.message || 'Failed to update member permissions');
      }
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError('Failed to update member permissions: ' + (err.message || 'Unknown error'));
    }
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  if (loading) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page settings-page" style={{ padding: pagePad }}>
          <h1 className="today-h1" style={{ marginBottom: '1.5rem' }}>Team &amp; integrations</h1>
          <div className="today-panel settings-section-panel settings-team-panel">
            <div className="today-panel-header">
              <Skeleton width="48px" height="15px" radius="4px" />
            </div>
            <TeamMembersSkeleton rows={3} />
          </div>
        </div>
      </div>
    );
  }

  function formatCustomerId(id) {
    const s = String(id).replace(/\D/g, '');
    return s.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }

  async function handleAdsConnect() {
    setAdsConnecting(true);
    setAdsError(null);
    setAdsTestResult(null);
    try {
      const res = await googleAdsApi.startConnect(selectedWorkspaceId);
      if (res.url) {
        window.location.href = res.url;
      } else {
        setAdsError(res.error || 'Failed to start connection');
        setAdsConnecting(false);
      }
    } catch (err) {
      setAdsError('Failed to start connection');
      setAdsConnecting(false);
    }
  }

  function selectedAdsAccount() {
    return adsAccounts.find((a) => a.customerId === adsSelectedId) || null;
  }

  async function handleAdsCancelPicker() {
    clearAdsPickerFlow();
    setAdsError(null);
    setAdsTestResult(null);
    setAdsLoading(true);
    try {
      const res = await googleAdsApi.status(selectedWorkspaceId);
      if (res.error) {
        setAdsError(res.error);
      } else {
        setAdsStatus(res);
      }
    } catch (err) {
      setAdsError('Failed to load Google Ads status');
    } finally {
      setAdsLoading(false);
    }
  }

  async function handleAdsFinish() {
    const picked = selectedAdsAccount();
    if (!picked || picked.isManager) return;

    setAdsFinishing(true);
    setAdsError(null);
    try {
      const res = await googleAdsApi.finishConnect(picked.customerId, {
        loginCustomerId: picked.suggestedLoginCustomerId,
        workspaceId: selectedWorkspaceId,
      });
      if (res.error) {
        setAdsError(res.error);
      } else {
        adsPickerFlowActive.current = false;
        setAdsStatus({
          connected: true,
          customerId: picked.customerId,
          conversionActionId: res.conversionActionId,
          accountDescriptiveName: res.accountDescriptiveName || picked.descriptiveName,
        });
        setAdsAccounts([]);
        setAdsSelectedId('');
      }
    } catch (err) {
      setAdsError('Failed to finish setup');
    } finally {
      setAdsFinishing(false);
    }
  }

  async function handleAdsChangeAccount() {
    setAdsError(null);
    setAdsTestResult(null);
    setAdsLoading(true);
    try {
      const res = await googleAdsApi.listAccounts(selectedWorkspaceId);
      if (res.error) {
        setAdsError(res.error);
      } else {
        adsPickerFlowActive.current = true;
        setAdsAccounts(normalizeAdsAccounts(res.accounts || res.customers));
        setAdsStatus(null);
        setAdsSelectedId('');
      }
    } catch (err) {
      setAdsError('Could not load accounts');
    } finally {
      setAdsLoading(false);
    }
  }

  async function handleAdsTestConversion() {
    setAdsTesting(true);
    setAdsError(null);
    setAdsTestResult(null);
    try {
      const res = await googleAdsApi.testConversion(selectedWorkspaceId);
      if (res.error) {
        setAdsError(res.error);
      } else {
        setAdsTestResult(res);
      }
    } catch (err) {
      setAdsError(err.message || 'Test conversion failed');
    } finally {
      setAdsTesting(false);
    }
  }

  if (accessDenied) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page settings-page" style={{ padding: pagePad }}>
          <div className="today-access-card">
            <h2 style={{ marginTop: 0, color: 'var(--ink)' }}>Access denied</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
              Only workspace owners can manage team members and permissions.
            </p>
            <p style={{ color: 'var(--muted)', marginBottom: 0, lineHeight: 1.5 }}>
              If you need to manage team settings, please contact the workspace owner.
            </p>
          </div>
        </div>
      </div>
    );
  }

  function renderMemberRoleChip(member) {
    if (member.role === 'owner') {
      return <span className="inbox-chip">Owner</span>;
    }
    if (member.status === 'pending') {
      return <span className="inbox-chip warn">Pending</span>;
    }
    return <span className="inbox-chip">Member</span>;
  }

  function renderMemberSubline(member) {
    if (member.status === 'pending') {
      return 'Invitation pending';
    }
    if (member.role === 'owner') {
      return 'Workspace owner - Full access';
    }
    return `Can access forms${member.leadAccess ? ', leads' : ''}${member.aiReplyAccess ? ', AI Reply settings' : ''}`;
  }

  function renderMemberActions(member) {
    if (member.role === 'owner' || !isOwner) {
      return null;
    }

    if (member.status === 'pending') {
      return (
        <div className="fb-table-actions">
          <button
            type="button"
            className="inbox-pill-btn-primary"
            onClick={() => handleResendInvite(member.id)}
          >
            Resend
          </button>
          <InlineConfirm
            message="Cancel this invitation?"
            onConfirm={() => handleRemoveMember(member.id)}
            buttonLabel="Cancel"
            buttonStyle={dangerConfirmBtnStyle}
          />
        </div>
      );
    }

    if (member.status === 'active') {
      return (
        <div className="fb-table-actions">
          <InlineConfirm
            message="Remove this team member?"
            onConfirm={() => handleRemoveMember(member.userId || member.id)}
            buttonLabel="Remove"
            buttonStyle={dangerConfirmBtnStyle}
          />
        </div>
      );
    }

    return (
      <InlineConfirm
        message="Remove this team member?"
        onConfirm={() => handleRemoveMember(member.userId || member.id)}
        buttonLabel="Remove"
        buttonStyle={dangerConfirmBtnStyle}
      />
    );
  }

  function renderMemberRowMobile(member) {
    return (
      <div key={member.id || member.userId} className="settings-team-mobile-row">
        <div className="settings-team-mobile-top">
          <div className="settings-member-name">{member.name || member.email}</div>
          {renderMemberRoleChip(member)}
        </div>
        {member.name && (
          <span className="settings-member-email">{member.email}</span>
        )}
        <div className="settings-member-subline">{renderMemberSubline(member)}</div>
        {(member.role !== 'owner' && isOwner) && (
          <>
            {member.status === 'active' && (
              <div className="settings-team-mobile-toggles">
                <label className="fb-checkbox-row" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={member.leadAccess || false}
                    onChange={(e) => handleUpdatePermissions(
                      member.userId || member.id,
                      e.target.checked,
                      member.aiReplyAccess || false
                    )}
                  />
                  <span>Lead access</span>
                </label>
                <label className="fb-checkbox-row" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={member.aiReplyAccess || false}
                    onChange={(e) => handleUpdatePermissions(
                      member.userId || member.id,
                      member.leadAccess || false,
                      e.target.checked
                    )}
                  />
                  <span>AI reply</span>
                </label>
              </div>
            )}
            <div className="settings-team-mobile-actions">
              {renderMemberActions(member)}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page settings-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <h1 className="today-h1">Team &amp; integrations</h1>
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              Team and permissions for this workspace
            </p>
          </div>
          {isOwner && (
            <button
              type="button"
              className={showInviteForm ? 'inbox-pill-btn' : 'inbox-pill-btn-primary'}
              onClick={() => setShowInviteForm(!showInviteForm)}
              style={{ flexShrink: 0, minHeight: isMobile ? 44 : undefined }}
            >
              {showInviteForm ? 'Cancel' : '+ Invite Team Member'}
            </button>
          )}
        </div>

        {error && (
          <div className="today-load-error">{error}</div>
        )}

        {success && (
          <div className="fb-callout success settings-flash-success">{success}</div>
        )}

        {isOwner && ['professional', 'enterprise', 'custom'].includes(ownerTier) && (
          <div className="today-panel settings-section-panel" style={{ marginBottom: '1rem' }}>
            <div className="today-panel-header">
              <span className="today-panel-title">Operator branding</span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hideAnsweredBy}
                disabled={brandingSaving}
                onChange={async (e) => {
                  if (!selectedWorkspaceId) return;
                  const next = e.target.checked;
                  setBrandingSaving(true);
                  try {
                    await updateDoc(doc(db, 'workspaces', selectedWorkspaceId), { hideAnsweredBy: next });
                    setHideAnsweredBy(next);
                    setSuccess(next ? 'Hidden Answered by gocta on AI email and chat.' : 'Answered by gocta is visible again.');
                  } catch (err) {
                    setError(err?.message || 'Could not save branding setting.');
                  } finally {
                    setBrandingSaving(false);
                  }
                }}
              />
              Hide &quot;Answered by gocta&quot; on AI emails and chat
            </label>
          </div>
        )}

        <div className="today-panel settings-section-panel settings-ads-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Google Ads</span>
          </div>
          <div className="settings-ads-body">
            <p className="settings-ads-desc">
              Connect a Google Ads account to enable conversion tracking for your forms.
            </p>

            {adsLoading ? (
              <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.9rem' }}>Loading Google Ads status…</p>
            ) : adsError ? (
              <div className="today-load-error" style={{ marginBottom: 0 }}>{adsError}</div>
            ) : adsAccounts.length > 0 ? (
              <div>
                <p style={{ fontWeight: 600, margin: '0 0 0.75rem', color: 'var(--ink)' }}>
                  Select a client account to connect:
                </p>
                <p style={{ margin: '0 0 0.75rem', color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.45 }}>
                  Pick the account where your ads run — not the manager (MCC) account.
                </p>
                <div className="settings-ads-accounts">
                  {adsAccounts.map((account) => {
                    const id = account.customerId;
                    const disabled = account.isManager;
                    return (
                      <label
                        key={id}
                        className={`settings-ads-account-option${disabled ? ' is-disabled' : ''}`}
                        style={disabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                      >
                        <input
                          type="radio"
                          name="adsAccount"
                          value={id}
                          checked={adsSelectedId === id}
                          disabled={disabled}
                          onChange={() => setAdsSelectedId(id)}
                        />
                        <span>
                          <span style={{ fontWeight: 600 }}>{account.descriptiveName || formatCustomerId(id)}</span>
                          <span className="mono" style={{ display: 'block', fontSize: '0.82rem', color: 'var(--muted)' }}>
                            {formatCustomerId(id)}
                            {account.isManager ? ' · Manager (MCC)' : ''}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    onClick={handleAdsFinish}
                    disabled={!adsSelectedId || adsFinishing || selectedAdsAccount()?.isManager}
                  >
                    {adsFinishing ? 'Finishing setup…' : 'Finish setup'}
                  </button>
                  <button
                    type="button"
                    className="inbox-pill-btn-secondary"
                    onClick={handleAdsCancelPicker}
                    disabled={adsFinishing}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : adsStatus?.connected ? (
              <div className="settings-ads-connected">
                <span className="inbox-chip accent-soft">Connected</span>
                <div className="settings-ads-account" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {adsStatus.accountDescriptiveName && (
                    <span style={{ fontWeight: 600 }}>{adsStatus.accountDescriptiveName}</span>
                  )}
                  <span className="mono">{formatCustomerId(adsStatus.customerId)}</span>
                  {adsStatus.conversionActionId && (
                    <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>
                      Closed-deal action ID: {adsStatus.conversionActionId}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="inbox-pill-btn-secondary"
                    onClick={handleAdsTestConversion}
                    disabled={adsTesting}
                  >
                    {adsTesting ? 'Sending test…' : 'Send test conversion'}
                  </button>
                  <button
                    type="button"
                    className="inbox-pill-btn-secondary"
                    onClick={handleAdsChangeAccount}
                  >
                    Change account
                  </button>
                </div>
                {adsTestResult?.message && (
                  <p
                    className="fb-callout success settings-flash-success"
                    style={{ marginTop: '0.75rem', marginBottom: 0 }}
                  >
                    {adsTestResult.message}
                  </p>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={handleAdsConnect}
                disabled={adsConnecting}
              >
                {adsConnecting ? 'Redirecting…' : 'Connect Google Ads'}
              </button>
            )}
          </div>
        </div>

        {isOwner && (
          <div className="today-panel settings-section-panel" style={{ marginBottom: '1.5rem' }}>
            <div className="today-panel-header">
              <span className="today-panel-title">Text messaging (SMS)</span>
            </div>
            <div className="settings-ads-body">
              <p className="settings-ads-desc">
                {workspaceSmsLive
                  ? 'Two-way AI SMS is live for this workspace. Manage your number, A2P registration, and inbox replies.'
                  : 'Provision a dedicated number, register for A2P compliance, and enable two-way SMS from your inbox.'}
              </p>
              <Link to="/text-messaging" className="inbox-pill-btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                {workspaceSmsLive ? 'Manage text messaging' : 'Open SMS setup'}
              </Link>
            </div>
          </div>
        )}

        {isOwner && (
          <div className="today-panel settings-section-panel" style={{ marginBottom: '1.5rem' }}>
            <div className="today-panel-header">
              <span className="today-panel-title">Calls</span>
            </div>
            <div className="settings-ads-body">
              <p className="settings-ads-desc">
                Forward calls to your phone and text back missed calls — set up on{' '}
                <Link to="/text-messaging">Text Messaging</Link>.
              </p>
              <p className="settings-ads-desc" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                Coming later: AI call answering
              </p>
              <label className="fb-checkbox-row" style={{ marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={voiceWaitlist}
                  disabled={voiceWaitlistSaving}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    setVoiceWaitlistSaving(true);
                    setError(null);
                    try {
                      const res = await smsApi.updateVoiceWaitlist({
                        workspaceId: selectedWorkspaceId,
                        voiceWaitlist: next,
                      });
                      if (!res.success) {
                        setError(res.error || 'Failed to update voice waitlist');
                        return;
                      }
                      setVoiceWaitlist(next);
                      setSuccess(next ? 'Added to voice early access waitlist' : 'Removed from voice waitlist');
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setVoiceWaitlistSaving(false);
                    }
                  }}
                />
                <span>Notify me about voice early access</span>
              </label>
            </div>
          </div>
        )}

        <div className="today-panel settings-section-panel settings-team-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Team</span>
          </div>

          {showInviteForm && isOwner && (
            <div className="settings-invite-block">
              <h3>Invite Team Member</h3>

              <div style={{ marginBottom: '1rem' }}>
                <label className="fb-label">
                  Email Address <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="email"
                  className="fb-input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="team@example.com"
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="fb-label">Name (Optional)</label>
                <input
                  type="text"
                  className="fb-input"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Business name or person's name"
                />
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                  This helps identify team members more easily. Leave blank to use email address.
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="fb-label">Permissions</label>
                <div className="settings-invite-perms">
                  <label className="fb-checkbox-row">
                    <input
                      type="checkbox"
                      checked={inviteLeadAccess}
                      onChange={(e) => setInviteLeadAccess(e.target.checked)}
                    />
                    <div>
                      <strong>Access Leads</strong>
                      <p>Allow this team member to view and manage leads</p>
                    </div>
                  </label>
                  <label className="fb-checkbox-row">
                    <input
                      type="checkbox"
                      checked={inviteAiReplyAccess}
                      onChange={(e) => setInviteAiReplyAccess(e.target.checked)}
                    />
                    <div>
                      <strong>Access AI Reply</strong>
                      <p>Allow this team member to configure AI email replies</p>
                    </div>
                  </label>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: '0.5rem', marginBottom: 0 }}>
                  All team members can create and edit forms. Use these permissions to control access to leads and AI Reply settings.
                </p>
              </div>

              {pendingResendInvite ? (
                <div className="settings-invite-actions">
                  <span style={{ fontSize: '0.9rem', color: 'var(--ink)' }}>
                    There&apos;s already a pending invitation for {pendingResendInvite.email}. Resend it?
                  </span>
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    onClick={() => {
                      handleResendInvite(pendingResendInvite.id);
                      setPendingResendInvite(null);
                    }}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    onClick={() => setPendingResendInvite(null)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={handleInvite}
                  disabled={inviting || !inviteEmail}
                >
                  {inviting ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
              )}
            </div>
          )}

          {members.length === 0 ? (
            <div className="today-empty">No team members yet. Invite someone to get started!</div>
          ) : isMobile ? (
            <div>{members.map((member) => renderMemberRowMobile(member))}</div>
          ) : (
            <div className="fb-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table className="fb-table settings-team-table">
                <thead>
                  <tr>
                    <th className="fb-table-head-cell settings-col-member">Member</th>
                    <th className="fb-table-head-cell settings-col-role">Role</th>
                    <th className="fb-table-head-cell settings-col-lead">Lead access</th>
                    <th className="fb-table-head-cell settings-col-ai">AI reply</th>
                    <th className="fb-table-head-cell settings-col-actions fb-table-head-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id || member.userId} className="fb-table-row">
                      <td className="fb-table-cell">
                        <div className="settings-member-name">{member.name || member.email}</div>
                        {member.name && (
                          <span className="settings-member-email">{member.email}</span>
                        )}
                        <div className="settings-member-subline">{renderMemberSubline(member)}</div>
                      </td>
                      <td className="fb-table-cell">
                        {renderMemberRoleChip(member)}
                      </td>
                      <td className="fb-table-cell settings-col-lead">
                        {member.status === 'active' && member.role !== 'owner' && isOwner ? (
                          <label className="fb-checkbox-row" style={{ marginBottom: 0, justifyContent: 'center' }}>
                            <input
                              type="checkbox"
                              checked={member.leadAccess || false}
                              onChange={(e) => handleUpdatePermissions(
                                member.userId || member.id,
                                e.target.checked,
                                member.aiReplyAccess || false
                              )}
                            />
                          </label>
                        ) : (
                          <span style={{ color: 'var(--faint)' }}>—</span>
                        )}
                      </td>
                      <td className="fb-table-cell settings-col-ai">
                        {member.status === 'active' && member.role !== 'owner' && isOwner ? (
                          <label className="fb-checkbox-row" style={{ marginBottom: 0, justifyContent: 'center' }}>
                            <input
                              type="checkbox"
                              checked={member.aiReplyAccess || false}
                              onChange={(e) => handleUpdatePermissions(
                                member.userId || member.id,
                                member.leadAccess || false,
                                e.target.checked
                              )}
                            />
                          </label>
                        ) : (
                          <span style={{ color: 'var(--faint)' }}>—</span>
                        )}
                      </td>
                      <td className="fb-table-cell fb-table-cell-actions">
                        {renderMemberActions(member) || <span style={{ color: 'var(--faint)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <LeadRoutingSettings
          workspaceId={selectedWorkspaceId}
          currentUser={currentUser}
          members={members}
          isOwner={isOwner}
          onError={setError}
          onSuccess={setSuccess}
        />

        <div className="today-panel settings-section-panel settings-perms-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Permissions</span>
          </div>
          <div className="settings-perms-body">
            <ul className="settings-perms-list">
              <li><strong>Owner:</strong> Full access to everything</li>
              <li><strong>All Team Members:</strong> Can create and edit forms, get embed codes</li>
              <li><strong>Lead Access:</strong> Allows viewing and managing leads (optional permission)</li>
              <li><strong>AI Reply Access:</strong> Allows configuring AI email replies (optional permission)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
